import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import NewsLayout from "../../components/news/NewsLayout";
import { Skeleton } from "../../components/ui/skeleton";
import { Clock, ArrowRight } from "lucide-react";
import { useProject } from "../../context/ProjectContext";
import { themeFromHost, siteNameFromHost } from "../../lib/themeFromHost";
import Seo, { organizationLd, webSiteLd } from "../../components/news/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const formatTimeAgo = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "";
  }
};

const placeholderImg = (theme, idx) => {
  if (theme === "espresso") {
    const set = [
      "https://images.unsplash.com/photo-1542372147193-a7aca54189cd?auto=format&fit=crop&w=1600&q=80",
      "https://images.unsplash.com/photo-1598544919456-fcb105fa7a6f?auto=format&fit=crop&w=1600&q=80",
      "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1517400508447-f8dd518b86db?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1497515114629-f71d768fd07c?auto=format&fit=crop&w=1200&q=80",
    ];
    return set[idx % set.length];
  }
  // rio
  const set = [
    "https://images.pexels.com/photos/15920138/pexels-photo-15920138.jpeg?auto=compress&w=1600",
    "https://images.unsplash.com/photo-1625556841726-ee3959350033?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1585829365295-ab7cd400c167?auto=format&fit=crop&w=1200&q=80",
  ];
  return set[idx % set.length];
};

const NewsHome = () => {
  const { publicProject, publicProjectLoading } = useProject();
  const [articles, setArticles] = useState([]);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publicProjectLoading) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        if (publicProject) {
          const [aRes, sRes] = await Promise.all([
            axios.get(`${API}/public/articles?project_id=${publicProject.id}&limit=20`),
            axios.get(`${API}/public/projects/${publicProject.id}/seo`).catch(() => ({ data: null })),
          ]);
          setArticles(aRes.data);
          setSeoData(sRes.data);
        } else {
          setArticles([]);
        }
      } catch (e) {
        console.error("Failed to fetch articles:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [publicProject, publicProjectLoading]);

  const theme = publicProject?.theme || themeFromHost();
  const siteName = publicProject?.name || siteNameFromHost() || "News";
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const faqs = Array.isArray(seoData?.faqs) ? seoData.faqs : [];

  // Build a combined JSON-LD array (Organization + WebSite (SearchAction) + FAQPage if FAQs exist)
  const seoConfig = seoData?.seo || {};
  const heroImage = articles?.[0]?.featured_image || articles?.[0]?.image_url || seoConfig.default_og_image;
  const heroTitle = articles?.[0]?.title;
  const tagline = publicProject?.description || (theme === "espresso"
    ? "Slow journalism for fast mornings — curated, thoughtful, delivered with the precision of a perfect pour."
    : theme === "rio"
      ? "Breaking news, every minute. Raw wires, unfiltered updates, real-time briefings."
      : `${siteName} — independent news coverage.`);
  const homeTitle = theme === "rio" ? "Breaking news, live" : "Independent morning briefing";
  const ld = [
    organizationLd({
      siteName,
      url: siteUrl,
      logoUrl: publicProject?.logo || seoConfig.organization_logo || `${siteUrl}/favicon.ico`,
      description: tagline,
      sameAs: Array.isArray(seoConfig.organization_same_as) ? seoConfig.organization_same_as : undefined,
    }),
    webSiteLd({
      siteName,
      url: siteUrl + "/",
      description: tagline,
      language: "en",
    }),
  ];
  if (faqs.length > 0) {
    ld.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
  }

  return (
    <NewsLayout theme={theme} siteName={siteName}>
      <Seo
        title={homeTitle}
        description={tagline}
        siteName={siteName}
        canonical={siteUrl + "/"}
        type="website"
        image={heroImage}
        imageAlt={heroTitle ? `${heroTitle} — ${siteName} lead story` : `${siteName} homepage`}
        imageWidth={1200}
        imageHeight={630}
        twitterSite={seoConfig.twitter_handle}
        jsonLd={ld}
      />
      {theme === "espresso" && <EspressoHome articles={articles} loading={loading} project={publicProject} faqs={faqs} />}
      {theme === "rio" && <RioHome articles={articles} loading={loading} project={publicProject} faqs={faqs} />}
      {theme !== "espresso" && theme !== "rio" && <BusinessHome articles={articles} loading={loading} />}
    </NewsLayout>
  );
};

/* ============================================================
 * ESPRESSO — Editorial bento grid
 * ============================================================ */
const EspressoHome = ({ articles, loading, project, faqs = [] }) => {
  if (loading || !articles.length) return <LoadingState theme="espresso" />;
  const [hero, ...rest] = articles;
  const secondary = rest.slice(0, 2);
  const grid = rest.slice(2, 8);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-24">
      {/* dateline */}
      <p className="font-meta mb-8" style={{ color: "var(--em-text-muted)" }}>
        {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} · Issue #{Math.max(1, articles.length)}
      </p>

      {/* tag-line */}
      <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-12 max-w-4xl" style={{ color: "var(--em-text)" }}>
        Good morning. Here is the world before your second cup.
      </h1>

      {/* Bento Grid */}
      <div className="grid grid-cols-12 gap-8">
        {/* Featured hero */}
        <article className="col-span-12 lg:col-span-8 group">
          <Link to={`/article/${hero.slug}`} className="block em-image-wrap mb-6 aspect-[16/10] relative overflow-hidden">
            <img
              src={hero.featured_image || placeholderImg("espresso", 0)}
              alt={hero.image_alt || `${hero.title} — ${hero.category} lead story`}
              width="1200"
              height="750"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </Link>
          <p className="font-meta mb-3" style={{ color: "var(--em-accent)" }}>
            {hero.category}
          </p>
          <Link to={`/article/${hero.slug}`}>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4 hover:opacity-80 transition-opacity" style={{ color: "var(--em-text)" }}>
              {hero.title}
            </h2>
          </Link>
          {hero.excerpt && (
            <p className="text-lg leading-relaxed mb-4 max-w-2xl" style={{ color: "var(--em-text-muted)" }}>
              {hero.excerpt}
            </p>
          )}
          <p className="font-meta" style={{ color: "var(--em-text-muted)" }}>
            By {hero.author_name || "Editorial"} · {formatDate(hero.created_at)}
          </p>
        </article>

        {/* Two secondary */}
        <aside className="col-span-12 lg:col-span-4 space-y-10">
          {secondary.map((a, i) => (
            <Link key={a.id} to={`/article/${a.slug}`} className="block group">
              <div className="em-image-wrap aspect-[4/3] mb-4 overflow-hidden">
                <img src={a.featured_image || placeholderImg("espresso", i + 1)} alt={a.image_alt || `${a.title} — ${a.category} story`} width="600" height="450" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <p className="font-meta mb-2" style={{ color: "var(--em-accent)" }}>{a.category}</p>
              <h3 className="font-display text-xl md:text-2xl font-bold leading-snug mb-2 group-hover:opacity-80 transition-opacity" style={{ color: "var(--em-text)" }}>
                {a.title}
              </h3>
              <p className="font-meta" style={{ color: "var(--em-text-muted)" }}>
                {formatDate(a.created_at)}
              </p>
            </Link>
          ))}
        </aside>
      </div>

      {/* Divider */}
      <div className="border-t my-16" style={{ borderColor: "var(--em-border)" }} />

      {/* More stories */}
      {grid.length > 0 && (
        <section>
          <header className="flex items-end justify-between mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold" style={{ color: "var(--em-text)" }}>
              More to savor
            </h2>
            <Link to={`/category/${project?.categories?.[0] || "business"}`} className="font-meta inline-flex items-center gap-2 hover:gap-3 transition-all" style={{ color: "var(--em-accent)" }}>
              All sections <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {grid.map((a, i) => (
              <Link key={a.id} to={`/article/${a.slug}`} className="block group">
                <div className="em-image-wrap aspect-[5/4] mb-5 overflow-hidden">
                  <img src={a.featured_image || placeholderImg("espresso", i + 3)} alt={a.image_alt || `${a.title} — ${a.category} story`} width="600" height="480" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <p className="font-meta mb-2" style={{ color: "var(--em-accent)" }}>{a.category}</p>
                <h3 className="font-display text-xl font-bold leading-snug mb-3 group-hover:opacity-80 transition-opacity" style={{ color: "var(--em-text)" }}>
                  {a.title}
                </h3>
                {a.excerpt && (
                  <p className="text-sm leading-relaxed line-clamp-2" style={{ color: "var(--em-text-muted)" }}>
                    {a.excerpt}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

/* ============================================================
 * RIO — Brutalist breaking-news control room grid
 * ============================================================ */
const RioHome = ({ articles, loading, project, faqs = [] }) => {
  if (loading || !articles.length) return <LoadingState theme="rio" />;
  const [hero, ...rest] = articles;
  const sidebar = rest.slice(0, 4);
  const grid = rest.slice(4, 14);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-6 pb-16">
      {/* SEO H1 — visually hidden; the visible hero uses <h2> for brutalist scale */}
      <h1 className="sr-only">
        {project?.name || "News Rio"} — Live breaking news wire, real-time updates and market briefings
      </h1>
      {/* status strip */}
      <div className="flex items-center gap-4 mb-6 text-xs font-mono uppercase" style={{ color: "var(--rio-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
        <span className="inline-flex items-center gap-2">
          <span className="rio-live-dot" /> LIVE FEED
        </span>
        <span>·</span>
        <span>{new Date().toUTCString().replace("GMT", "UTC")}</span>
        <span>·</span>
        <span>{articles.length} STORIES</span>
      </div>

      {/* Control room grid */}
      <div className="grid grid-cols-12 gap-0 mb-12" style={{ border: "1px solid var(--rio-border)" }}>
        {/* Massive hero — left 8 cols */}
        <article className="col-span-12 lg:col-span-8 relative group" style={{ borderRight: "1px solid var(--rio-border)" }}>
          <Link to={`/article/${hero.slug}`} className="block relative aspect-[16/10] overflow-hidden">
            <img src={hero.featured_image || placeholderImg("rio", 0)} alt={hero.image_alt || `${hero.title} — ${hero.category} breaking story`} width="1200" height="750" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" loading="eager" fetchPriority="high" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.1) 60%)" }} />
            <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-10">
              <p className="font-mono text-xs uppercase mb-4" style={{ color: "var(--rio-accent)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em" }}>
                <span className="rio-live-dot mr-2" />
                {hero.category} · {formatTimeAgo(hero.created_at)}
              </p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]" style={{ color: "var(--rio-text)" }}>
                {hero.title}
              </h2>
            </div>
          </Link>
        </article>

        {/* Sidebar — 4 stories stacked */}
        <aside className="col-span-12 lg:col-span-4 grid grid-cols-1">
          {sidebar.map((a, i) => (
            <Link key={a.id} to={`/article/${a.slug}`} className="block p-5 transition-all hover:bg-[var(--rio-surface)]" style={{ borderBottom: i < sidebar.length - 1 ? "1px solid var(--rio-border)" : undefined }}>
              <p className="font-mono text-xs uppercase mb-2" style={{ color: "var(--rio-accent)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em" }}>
                {a.category} · {formatTimeAgo(a.created_at)}
              </p>
              <h3 className="font-display uppercase leading-tight" style={{ color: "var(--rio-text)", fontSize: "1.35rem" }}>
                {a.title}
              </h3>
            </Link>
          ))}
        </aside>
      </div>

      {/* All news ticker */}
      {grid.length > 0 && (
        <section>
          <header className="flex items-center justify-between mb-6 pb-3" style={{ borderBottom: "2px solid var(--rio-accent)" }}>
            <h2 className="font-display text-3xl md:text-4xl uppercase" style={{ color: "var(--rio-text)" }}>
              All Wires
            </h2>
            <Link to={`/category/${project?.categories?.[0] || "business"}`} className="font-mono text-xs uppercase tracking-widest hover:text-white" style={{ color: "var(--rio-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
              &gt; ALL SECTIONS
            </Link>
          </header>

          <div className="grid grid-cols-12 gap-0" style={{ border: "1px solid var(--rio-border)" }}>
            {grid.map((a, i) => {
              const span = "col-span-12 sm:col-span-6 lg:col-span-3";
              return (
                <Link key={a.id} to={`/article/${a.slug}`} className={`${span} block group transition-colors`} style={{ borderRight: "1px solid var(--rio-border)", borderBottom: "1px solid var(--rio-border)" }}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={a.featured_image || placeholderImg("rio", i + 1)} alt={a.image_alt || `${a.title} — ${a.category} wire`} width="600" height="450" loading="lazy" className="absolute inset-0 w-full h-full object-cover grayscale transition-all duration-300 group-hover:grayscale-0 group-hover:scale-[1.04]" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent 50%)" }} />
                  </div>
                  <div className="p-4">
                    <p className="font-mono text-[0.65rem] uppercase mb-2" style={{ color: "var(--rio-accent)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em" }}>
                      {a.category} · {formatTimeAgo(a.created_at)}
                    </p>
                    <h3 className="font-display text-base uppercase leading-tight" style={{ color: "var(--rio-text)" }}>
                      {a.title}
                    </h3>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="mt-16" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="font-display uppercase text-3xl md:text-4xl mb-6 pb-3" style={{ color: "var(--rio-text)", borderBottom: "2px solid var(--rio-accent)" }}>
            FAQ
          </h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-0" style={{ border: "1px solid var(--rio-border)" }}>
            {faqs.map((f, i) => (
              <div key={i} className="p-5" style={{ borderRight: i % 2 === 0 ? "1px solid var(--rio-border)" : undefined, borderBottom: "1px solid var(--rio-border)" }}>
                <dt className="font-mono text-xs uppercase mb-2" style={{ color: "var(--rio-accent)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em" }}>Q{String(i + 1).padStart(2, "0")}</dt>
                <p className="font-display uppercase text-lg mb-3" style={{ color: "var(--rio-text)" }}>{f.question}</p>
                <dd className="text-sm leading-relaxed" style={{ color: "var(--rio-text-muted)" }}>{f.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
};

/* ============================================================
 * BUSINESS fallback (kept simple)
 * ============================================================ */
const BusinessHome = ({ articles, loading }) => {
  if (loading) return <LoadingState theme="business" />;
  const [hero, ...rest] = articles;
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <h1 className="text-4xl md:text-5xl font-bold mb-8">Latest News</h1>
      {hero ? (
        <Link to={`/article/${hero.slug}`} className="block mb-12">
          <div className="aspect-[16/8] bg-slate-200 rounded-2xl overflow-hidden mb-4">
            {hero.featured_image && <img src={hero.featured_image} alt={hero.title} className="w-full h-full object-cover" />}
          </div>
          <h2 className="text-3xl font-bold mb-2">{hero.title}</h2>
          {hero.excerpt && <p className="text-slate-600">{hero.excerpt}</p>}
        </Link>
      ) : (
        <p className="text-slate-500">No articles published yet.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {rest.map((a) => (
          <Link key={a.id} to={`/article/${a.slug}`} className="block">
            <div className="aspect-[4/3] bg-slate-200 rounded-xl overflow-hidden mb-3">
              {a.featured_image && <img src={a.featured_image} alt={a.title} className="w-full h-full object-cover" />}
            </div>
            <h3 className="font-bold mb-1">{a.title}</h3>
            <p className="text-sm text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{formatDate(a.created_at)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

const LoadingState = ({ theme }) => (
  <div className="max-w-7xl mx-auto px-6 py-12">
    <div className="grid grid-cols-12 gap-6">
      <div className={`${theme === "espresso" ? "col-span-12 lg:col-span-8" : "col-span-12 lg:col-span-8"}`}>
        <Skeleton className="aspect-[16/10] mb-4" style={theme === "rio" ? { borderRadius: 0 } : undefined} />
        <Skeleton className="h-12 w-3/4 mb-3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <div className="col-span-12 lg:col-span-4 space-y-8">
        {[1, 2].map((i) => (
          <div key={i}>
            <Skeleton className="aspect-[4/3] mb-3" style={theme === "rio" ? { borderRadius: 0 } : undefined} />
            <Skeleton className="h-6 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default NewsHome;
